import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Crown, TrendingUp, TrendingDown, Globe, PieChart, BarChart3,
  Building2, Briefcase, DollarSign, ArrowUpRight, ArrowDownRight,
  Shield, Target, LineChart, Percent, Calendar
} from 'lucide-react';
import { 
  PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, 
  AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, 
  LineChart as RechartsLine, Line, Legend
} from 'recharts';

const SovereignWealthFund = () => {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Try to get authenticated data, fall back to demo data
      const response = await axios.get(`${API}/public/swf-overview`);
      setPortfolio(response.data);
    } catch (error) {
      // Use demo data if API fails
      setPortfolio(getDemoData());
    } finally {
      setLoading(false);
    }
  };

  const getDemoData = () => ({
    total_assets: 892.5,
    unit: "billion USD",
    established_year: 2005,
    investment_horizon: "Long-term (7+ years)",
    risk_profile: "Moderate-Conservative",
    allocations: [
      { asset_class: "Global Equities", allocation: 35.0, value: 312.4, return_ytd: 12.5, benchmark: "MSCI World" },
      { asset_class: "Fixed Income", allocation: 30.0, value: 267.8, return_ytd: 4.2, benchmark: "Bloomberg Global Agg" },
      { asset_class: "Real Estate", allocation: 15.0, value: 133.9, return_ytd: 8.1, benchmark: "NCREIF Property" },
      { asset_class: "Infrastructure", allocation: 12.0, value: 107.1, return_ytd: 9.3, benchmark: "S&P Global Infra" },
      { asset_class: "Alternative Investments", allocation: 8.0, value: 71.4, return_ytd: 15.7, benchmark: "HFRI Fund Weighted" }
    ],
    geographic_distribution: [
      { region: "North America", percentage: 38, value: 339.2 },
      { region: "Europe", percentage: 28, value: 249.9 },
      { region: "Asia Pacific", percentage: 22, value: 196.4 },
      { region: "Emerging Markets", percentage: 12, value: 107.1 }
    ],
    performance: {
      ytd_return: 9.8,
      one_year: 11.2,
      three_year: 8.9,
      five_year: 8.5,
      ten_year: 7.8,
      since_inception: 7.2
    },
    historical_performance: [
      { year: '2019', return: 15.2, benchmark: 12.8 },
      { year: '2020', return: 8.4, benchmark: 6.2 },
      { year: '2021', return: 18.6, benchmark: 16.4 },
      { year: '2022', return: -8.2, benchmark: -12.1 },
      { year: '2023', return: 12.4, benchmark: 10.8 },
      { year: '2024', return: 9.8, benchmark: 8.5 }
    ],
    governance: {
      board_members: 9,
      investment_committee: 7,
      external_managers: 45,
      annual_reports_published: 19
    },
    top_holdings: [
      { name: "US Treasury Bonds", type: "Fixed Income", value: 89.2, percentage: 10.0 },
      { name: "Apple Inc.", type: "Equity", value: 15.8, percentage: 1.8 },
      { name: "Microsoft Corp.", type: "Equity", value: 14.2, percentage: 1.6 },
      { name: "London Office Portfolio", type: "Real Estate", value: 12.5, percentage: 1.4 },
      { name: "European Infrastructure Fund", type: "Infrastructure", value: 11.8, percentage: 1.3 }
    ],
    esg_metrics: {
      carbon_intensity: "45% below benchmark",
      renewable_investments: 28.5,
      esg_integration: "100% of portfolios",
      sustainable_bonds: 42.3
    }
  });

  const COLORS = ['#FFD700', '#1a1a1a', '#dc2626', '#6b7280', '#FFC107'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="swf-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 gold-gradient rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-black" />
              </div>
              <span className="font-serif text-lg font-semibold text-black">Royal CBDC</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/public">
                <Button variant="ghost" className="text-black/70 hover:text-black">Public Portal</Button>
              </Link>
              <Link to="/bonds">
                <Button variant="ghost" className="text-black/70 hover:text-black">Bonds</Button>
              </Link>
              <Link to="/login">
                <Button className="bg-black hover:bg-black/90 text-white" data-testid="swf-login-btn">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
              <Briefcase className="h-4 w-4 text-gold-light" />
              <span className="font-mono text-xs uppercase tracking-widest text-gold-light">National Investment Authority</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-6">
              Sovereign Wealth Fund
            </h1>
            <p className="text-lg md:text-xl text-white/70 mb-8 max-w-2xl mx-auto">
              Managing national assets for future generations through prudent, long-term investments across global markets.
            </p>
            <div className="flex flex-wrap justify-center gap-8 mt-12">
              <div className="text-center">
                <p className="font-mono text-4xl md:text-5xl font-bold text-gold-light" data-testid="swf-total-assets">
                  ${portfolio?.total_assets}B
                </p>
                <p className="text-sm text-white/60 mt-1 uppercase tracking-wider">Assets Under Management</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-4xl md:text-5xl font-bold text-white">
                  {portfolio?.performance?.since_inception}%
                </p>
                <p className="text-sm text-white/60 mt-1 uppercase tracking-wider">Annualized Return</p>
              </div>
              <div className="text-center">
                <p className="font-mono text-4xl md:text-5xl font-bold text-white">
                  {portfolio?.established_year}
                </p>
                <p className="text-sm text-white/60 mt-1 uppercase tracking-wider">Established</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Tabs defaultValue="overview" className="space-y-8">
          <TabsList className="bg-black/5 mx-auto flex w-fit">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="allocation" data-testid="tab-allocation">Asset Allocation</TabsTrigger>
            <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
            <TabsTrigger value="governance" data-testid="tab-governance">Governance</TabsTrigger>
            <TabsTrigger value="esg" data-testid="tab-esg">ESG & Sustainability</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total AUM', value: `$${portfolio?.total_assets}B`, icon: <DollarSign className="h-5 w-5" />, change: '+9.8%' },
                { label: 'YTD Return', value: `${portfolio?.performance?.ytd_return}%`, icon: <TrendingUp className="h-5 w-5" />, change: 'vs 8.5% benchmark' },
                { label: 'Asset Classes', value: '5', icon: <PieChart className="h-5 w-5" /> },
                { label: 'Global Reach', value: '45+ Countries', icon: <Globe className="h-5 w-5" /> }
              ].map((metric, idx) => (
                <Card key={idx} className="sovereign-card border-black/10">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-black/60">{metric.label}</span>
                      <div className="text-gold">{metric.icon}</div>
                    </div>
                    <p className="font-mono text-2xl font-bold text-black" data-testid={`metric-${idx}`}>
                      {metric.value}
                    </p>
                    {metric.change && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <ArrowUpRight className="h-3 w-3" /> {metric.change}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Investment Philosophy */}
            <Card className="sovereign-card border-black/10">
              <CardHeader>
                <CardTitle className="font-serif text-2xl font-normal flex items-center gap-3">
                  <Target className="h-6 w-6 text-gold" />
                  Investment Philosophy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { 
                      title: 'Long-Term Focus', 
                      description: 'Investment decisions driven by fundamental value creation over 7+ year horizons, not short-term market movements.',
                      icon: <Calendar className="h-8 w-8" />
                    },
                    { 
                      title: 'Global Diversification', 
                      description: 'Strategic allocation across geographies, asset classes, and sectors to optimize risk-adjusted returns.',
                      icon: <Globe className="h-8 w-8" />
                    },
                    { 
                      title: 'Responsible Investing', 
                      description: 'ESG factors integrated into all investment decisions, promoting sustainable economic growth.',
                      icon: <Shield className="h-8 w-8" />
                    }
                  ].map((item, idx) => (
                    <div key={idx} className="text-center p-6 rounded-lg bg-black/5">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center text-gold">
                        {item.icon}
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                      <p className="text-sm text-black/60">{item.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="sovereign-card border-black/10">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Asset Allocation Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={portfolio?.allocations}
                          dataKey="allocation"
                          nameKey="asset_class"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                        >
                          {portfolio?.allocations?.map((entry, idx) => (
                            <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="sovereign-card border-black/10">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Geographic Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={portfolio?.geographic_distribution} layout="vertical">
                        <XAxis type="number" axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="region" axisLine={false} tickLine={false} width={100} />
                        <Tooltip />
                        <Bar dataKey="percentage" fill="#FFD700" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Asset Allocation Tab */}
          <TabsContent value="allocation" className="space-y-8">
            <Card className="sovereign-card border-black/10">
              <CardHeader>
                <CardTitle className="font-serif text-2xl font-normal">Strategic Asset Allocation</CardTitle>
                <CardDescription>Target portfolio composition and current holdings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-black/10">
                        <th className="text-left py-4 text-sm font-semibold text-black/60">Asset Class</th>
                        <th className="text-right py-4 text-sm font-semibold text-black/60">Allocation</th>
                        <th className="text-right py-4 text-sm font-semibold text-black/60">Value (USD)</th>
                        <th className="text-right py-4 text-sm font-semibold text-black/60">YTD Return</th>
                        <th className="text-left py-4 text-sm font-semibold text-black/60">Benchmark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {portfolio?.allocations?.map((asset, idx) => (
                        <tr key={idx} className="border-b border-black/5" data-testid={`allocation-row-${idx}`}>
                          <td className="py-4 font-medium">{asset.asset_class}</td>
                          <td className="py-4 text-right font-mono">{asset.allocation}%</td>
                          <td className="py-4 text-right font-mono">${asset.value}B</td>
                          <td className={`py-4 text-right font-mono ${asset.return_ytd >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {asset.return_ytd >= 0 ? '+' : ''}{asset.return_ytd}%
                          </td>
                          <td className="py-4 text-black/60 text-sm">{asset.benchmark}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-black/5">
                        <td className="py-4 font-bold">Total Portfolio</td>
                        <td className="py-4 text-right font-mono font-bold">100%</td>
                        <td className="py-4 text-right font-mono font-bold">${portfolio?.total_assets}B</td>
                        <td className="py-4 text-right font-mono font-bold text-emerald-600">+{portfolio?.performance?.ytd_return}%</td>
                        <td className="py-4"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            <Card className="sovereign-card border-black/10">
              <CardHeader>
                <CardTitle className="font-sans text-lg">Top Holdings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {portfolio?.top_holdings?.map((holding, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-black/5" data-testid={`holding-${idx}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-gold" />
                        </div>
                        <div>
                          <p className="font-medium">{holding.name}</p>
                          <p className="text-sm text-black/60">{holding.type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-semibold">${holding.value}B</p>
                        <p className="text-sm text-black/60">{holding.percentage}% of portfolio</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              {[
                { period: 'YTD', value: portfolio?.performance?.ytd_return },
                { period: '1 Year', value: portfolio?.performance?.one_year },
                { period: '3 Year', value: portfolio?.performance?.three_year },
                { period: '5 Year', value: portfolio?.performance?.five_year },
                { period: '10 Year', value: portfolio?.performance?.ten_year },
                { period: 'Since Inception', value: portfolio?.performance?.since_inception }
              ].map((item, idx) => (
                <Card key={idx} className="sovereign-card border-black/10">
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-black/60 uppercase tracking-wider mb-2">{item.period}</p>
                    <p className={`font-mono text-2xl font-bold ${item.value >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`perf-${idx}`}>
                      {item.value >= 0 ? '+' : ''}{item.value}%
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="sovereign-card border-black/10">
              <CardHeader>
                <CardTitle className="font-sans text-lg">Historical Performance vs Benchmark</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={portfolio?.historical_performance}>
                      <XAxis dataKey="year" axisLine={false} tickLine={false} />
                      <YAxis axisLine={false} tickLine={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="return" name="Fund Return" fill="#FFD700" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="benchmark" name="Benchmark" fill="#1a1a1a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="sovereign-card border-black/10 bg-black text-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <Shield className="h-8 w-8 text-gold" />
                  <div>
                    <h3 className="font-semibold text-lg">Consistent Outperformance</h3>
                    <p className="text-white/60">Outperformed benchmark in 5 of the last 6 years</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="font-mono text-3xl font-bold text-gold">+1.3%</p>
                    <p className="text-sm text-white/60">Avg Annual Alpha</p>
                  </div>
                  <div>
                    <p className="font-mono text-3xl font-bold text-white">0.72</p>
                    <p className="text-sm text-white/60">Sharpe Ratio</p>
                  </div>
                  <div>
                    <p className="font-mono text-3xl font-bold text-white">8.5%</p>
                    <p className="text-sm text-white/60">Volatility</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Governance Tab */}
          <TabsContent value="governance" className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Board Members', value: portfolio?.governance?.board_members, icon: <Building2 /> },
                { label: 'Investment Committee', value: portfolio?.governance?.investment_committee, icon: <Briefcase /> },
                { label: 'External Managers', value: portfolio?.governance?.external_managers, icon: <Globe /> },
                { label: 'Annual Reports', value: portfolio?.governance?.annual_reports_published, icon: <BarChart3 /> }
              ].map((item, idx) => (
                <Card key={idx} className="sovereign-card border-black/10">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center text-gold">
                      {item.icon}
                    </div>
                    <p className="font-mono text-3xl font-bold">{item.value}</p>
                    <p className="text-sm text-black/60 mt-1">{item.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="sovereign-card border-black/10">
              <CardHeader>
                <CardTitle className="font-serif text-2xl font-normal">Governance Framework</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { title: 'Board of Directors', description: 'Independent board with fiduciary responsibility for fund oversight and strategic direction.' },
                    { title: 'Investment Committee', description: 'Expert committee reviewing and approving all significant investment decisions.' },
                    { title: 'Risk Management', description: 'Comprehensive risk framework with independent risk monitoring and reporting.' },
                    { title: 'Transparency', description: 'Quarterly reports, annual audits, and public disclosure of holdings and performance.' }
                  ].map((item, idx) => (
                    <div key={idx} className="p-6 rounded-lg border border-black/10">
                      <h3 className="font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-black/60">{item.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ESG Tab */}
          <TabsContent value="esg" className="space-y-8">
            <Card className="sovereign-card border-black/10 bg-gradient-to-r from-emerald-50 to-emerald-100">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Shield className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h2 className="font-serif text-2xl font-normal">Commitment to Sustainable Investing</h2>
                    <p className="text-black/60">Integrating ESG factors across all investment decisions</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Carbon Intensity', value: portfolio?.esg_metrics?.carbon_intensity, icon: <TrendingDown className="h-5 w-5" /> },
                { label: 'Renewable Investments', value: `$${portfolio?.esg_metrics?.renewable_investments}B`, icon: <Globe className="h-5 w-5" /> },
                { label: 'ESG Integration', value: portfolio?.esg_metrics?.esg_integration, icon: <Shield className="h-5 w-5" /> },
                { label: 'Sustainable Bonds', value: `$${portfolio?.esg_metrics?.sustainable_bonds}B`, icon: <Briefcase className="h-5 w-5" /> }
              ].map((item, idx) => (
                <Card key={idx} className="sovereign-card border-black/10">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-emerald-600">{item.icon}</div>
                    </div>
                    <p className="font-mono text-xl font-bold text-black" data-testid={`esg-${idx}`}>{item.value}</p>
                    <p className="text-sm text-black/60 mt-1">{item.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="sovereign-card border-black/10">
              <CardHeader>
                <CardTitle className="font-sans text-lg">ESG Priorities</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { title: 'Climate Action', description: 'Net-zero portfolio commitment by 2050, 45% reduction in carbon intensity achieved.', color: 'bg-emerald-500' },
                    { title: 'Social Responsibility', description: 'Engagement on labor practices, diversity, and human rights across portfolio companies.', color: 'bg-blue-500' },
                    { title: 'Good Governance', description: 'Active voting and engagement on board composition, executive pay, and ethics.', color: 'bg-gold' }
                  ].map((item, idx) => (
                    <div key={idx} className="relative overflow-hidden rounded-lg border border-black/10 p-6">
                      <div className={`absolute top-0 left-0 w-1 h-full ${item.color}`} />
                      <h3 className="font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-black/60">{item.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="bg-black text-white py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 gold-gradient rounded-sm flex items-center justify-center">
                <Crown className="h-6 w-6 text-black" />
              </div>
              <span className="font-serif text-xl font-semibold">Royal CBDC</span>
            </div>
            <p className="text-white/60 text-sm mb-4">
              Sovereign Wealth Fund • Managing National Assets Since {portfolio?.established_year}
            </p>
            <p className="text-white/40 text-xs">
              © 2024 Royal CBDC. All investment data is for demonstration purposes.
              Past performance does not guarantee future results.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SovereignWealthFund;
